import React, { useEffect, useState } from 'react';
import fp from 'lodash/fp';
import connect from 'react-redux/lib/connect/connect';
import { bindActionCreators } from 'redux';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import Loading from '../../../loading';
import { adminActions } from '../../../../../state/modules/admin';
import { fullName } from '../../../../../services';
import { InputFormField, FormSubmissionMessage } from '../../../../components';
import { useDebounce } from '../../../../../utils';

const defaultFetchQuery= {
  skip: 0,
  take: 20,
  tags: '',
}

const CopyUserCpdActivityModal = ({
  showCopyCpdActivityModal,
  toggleCopyCpdActivityModal,
  user,
  isCopyingActivity,
  externalUserData,
  getExternalUsers,
  copyUserCpdActivity,
  copyResult,
  error,
}) => {
  const showModal = showCopyCpdActivityModal === user.id;
  const externalUsers = externalUserData === undefined ? [] : fp.filter(
    (externalUser) => externalUser.id !== user.id
  )(externalUserData.users);

  const [fetchQuery, setFetchQuery] = useState(defaultFetchQuery);
  const [searchText, setSearchText] = useState('');
  const [selectedUser, setSelectedUser] = useState();
  const [optionsHidden, setOptionsHidden] = useState(true);
  const [showCopyMessage, setShowCopyMessage] = useState(false);

  const debouncedFetchQuery = useDebounce(fetchQuery, 500);

  const submissionMessage = copyResult !== null  && selectedUser !== undefined
    ? `Copied ${copyResult} activities from ${fullName(user)} (${user.email}) to ${fullName(selectedUser)} (${selectedUser.email})`
    : '';

  // Update fetch query when search text changed.
  useEffect(() => {
    if (showModal) {
      setFetchQuery({
        ...fetchQuery,
        tags: searchText,
      });
    }
  }, [showModal, searchText]);

  // Search for external users when fetch query updated.
  useEffect(() => {
    if (showModal) {
      getExternalUsers(debouncedFetchQuery);
    }
  }, [showModal, debouncedFetchQuery]);

  // Show copy result message when getting errors or results.
  useEffect(() => {
    if (showModal && (error !== null || copyResult !== null)) {
      setShowCopyMessage(true);
    } else {
      setShowCopyMessage(false);
    }
  }, [showModal, error, copyResult])

  const onSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const onClickUser = (user) => {
    setSelectedUser(user);
    setSearchText(fullName(user));
    setOptionsHidden(true);
  }

  const copyActivity = (previousUser, newUser) => {
    if (previousUser !== undefined && newUser !== undefined) {
      copyUserCpdActivity(previousUser.id, newUser.id);
    }
  };

  const closeSubmissionMessage = () => setShowCopyMessage(false);
  
  return (
    <Modal show={showModal} onHide={() => toggleCopyCpdActivityModal(user.id)}>
      <Modal.Header>
        <Modal.Title componentClass='h2'>
          {`Copy ${fullName(user)}'s CPD activities to:`}
        </Modal.Title>
        <Modal.Body>
          <FormSubmissionMessage
            showSubmissionMessage={showCopyMessage}
            submissionMessage={submissionMessage}
            error={error}
            hideSubmissionMessage={closeSubmissionMessage}
          ></FormSubmissionMessage>
          <form>
            <InputFormField
              id='external-user-input'
              label='External User'
              value={searchText}
              onChange={onSearchChange}
              inputProps={{
                autoComplete: "off",
                onSelect: () => setOptionsHidden(false),
              }}
            />
            {/* Custom drop down options for searched external users */}
            <div
              style={{
                minWidth: '530px',
                maxHeight: '300px',
                overflow: 'auto',
                border: '1px solid lightgrey',
                position: 'absolute',
                zIndex: 100,
                background: 'white',
                padding: '10px',
                paddingRight: '40px',
                marginTop: '50px',
              }}
              id="external-user-options"
              hidden={optionsHidden}
            >
              {/* Button to close the custom dropdown */}
              <Button
                bsStyle='plain'
                style={{
                  width: '20px',
                  height: '20px',
                  padding: 0,
                  marginTop: '10px',
                  marginRight: '10px',
                  position: 'absolute',
                  right: 0,
                  zIndex: 100,
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOptionsHidden(true)}
                }
              >
                x
              </Button>
              {
                // Matched external user options.
                externalUsers.length === 0 
                ? <p style={{ textAlign: 'center' }}>No external user found</p>
                : fp.map((user) =>
                    <input
                      key={user.id}
                      className={'select-option-button'}
                      onClick={() => onClickUser(user)}
                      value={`${fullName(user)} (${user.email})`}
                      type="button"
                      style={{
                        margin: '2px',
                        border: '1px solid lightgrey',
                      }}
                    />
                  )(externalUsers)
              }
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className='text-center confirmation-footer'>
          <Row>
            <Col xs={12}>
              <Button
                disabled={isCopyingActivity || copyResult !== null}
                onClick={(e) => { 
                  e.preventDefault();
                  e.stopPropagation();
                  copyActivity(user, selectedUser);
                }}
              >
                {isCopyingActivity ? <Loading /> : 'Transfer'}
              </Button>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <Button bsStyle='plain' onClick={() => toggleCopyCpdActivityModal(user.id)}>
                Cancel
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal.Header>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  isFetchingUser: state.admin.isFetchingUser,
  isCopyingActivity: state.admin.isCopyingActivity,
  externalUserData: state.admin.externalUserData,
  copyResult: state.admin.copyResult,
  error: state.admin.error,
});

const mapDispatchToProps = (dispatch) => ({
  getExternalUsers: bindActionCreators(adminActions.admin.getExternalUsers, dispatch),
  copyUserCpdActivity: bindActionCreators(adminActions.admin.copyUserCpdActivity, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(CopyUserCpdActivityModal);



// WEBPACK FOOTER //
// ./src/containers/pages/admin-portal/user-portal/partial/copy-user-cpd-activity-modal.js